import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

const SQUARE_SDK_URLS = {
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
  production: "https://web.squarecdn.com/v1/square.js",
};

// Lazily load the Square Web Payments SDK <script> tag once.
function loadSquareSdk(env) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const url = SQUARE_SDK_URLS[env] || SQUARE_SDK_URLS.sandbox;
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Square));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = url; s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function Checkout() {
  const { cart, clearCart, user, API, authHeaders, content } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ customer_name: user?.name || "", customer_phone: "", notes: "", payment_method: "cash_pickup" });
  const [loading, setLoading] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const [card, setCard] = useState(null);
  const [cashAppPay, setCashAppPay] = useState(null);
  const cardRef = useRef(null);
  const cashAppRef = useRef(null);
  const total = cart.reduce((s,x)=>s+x.price*x.qty,0);

  // Fetch Square public config once
  useEffect(() => {
    axios.get(`${API}/payments/public-config`).then(r => setPayCfg(r.data)).catch(()=>setPayCfg({square_configured:false}));
  }, [API]);

  // Initialize Square SDK when user picks an online method
  useEffect(() => {
    if (!payCfg?.square_configured) return;
    if (form.payment_method !== "square_card" && form.payment_method !== "cash_app_pay") return;
    let cancelled = false;
    let cardInst = null;
    let cashInst = null;
    (async () => {
      try {
        const Square = await loadSquareSdk(payCfg.square_environment);
        if (cancelled) return;
        const payments = Square.payments(payCfg.square_application_id, payCfg.square_location_id);
        if (form.payment_method === "square_card" && cardRef.current && !card) {
          cardInst = await payments.card();
          await cardInst.attach(cardRef.current);
          if (!cancelled) setCard(cardInst);
        }
        if (form.payment_method === "cash_app_pay" && cashAppRef.current && !cashAppPay) {
          const req = payments.paymentRequest({
            countryCode: "US",
            currencyCode: "USD",
            total: { amount: total.toFixed(2), label: "Total" },
          });
          cashInst = await payments.cashAppPay(req, { redirectURL: window.location.href, referenceId: `order-${Date.now()}` });
          await cashInst.attach(cashAppRef.current, { shape: "semiround", size: "medium" });
          if (!cancelled) setCashAppPay(cashInst);
        }
      } catch (err) {
        console.error("Square SDK init failed:", err);
        toast.error("Could not load Square payment form. Try another method.");
      }
    })();
    return () => {
      cancelled = true;
      if (cardInst?.destroy) cardInst.destroy().catch(()=>{});
      if (cashInst?.destroy) cashInst.destroy().catch(()=>{});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.payment_method, payCfg]);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const orderRes = await axios.post(`${API}/orders`, { ...form, items: cart }, { headers: authHeaders() });
      const orderId = orderRes.data.order_id;

      if (form.payment_method === "square_card" || form.payment_method === "cash_app_pay") {
        const inst = form.payment_method === "square_card" ? card : cashAppPay;
        if (!inst) throw new Error("Payment form not ready");
        const tokenResult = await inst.tokenize();
        if (tokenResult.status !== "OK") {
          const msg = tokenResult.errors?.[0]?.message || "Payment was not authorized";
          throw new Error(msg);
        }
        const verificationToken = tokenResult.token && form.payment_method === "square_card"
          ? (await inst.verifyBuyer?.(tokenResult.token, { intent: "CHARGE", amount: total.toFixed(2), currencyCode: "USD", billingContact: { givenName: form.customer_name } }).catch(()=>null))?.token
          : null;
        const charge = await axios.post(`${API}/payments/square`, {
          order_id: orderId,
          source_id: tokenResult.token,
          verification_token: verificationToken || undefined,
        }, { headers: authHeaders() });
        clearCart();
        toast.success(charge.data.paid ? "Payment received! Order confirmed." : `Payment ${charge.data.status}`);
        nav("/orders");
        return;
      }

      // COD / card-at-pickup paths
      clearCart();
      toast.success("Order placed! Pickup in Rossville.");
      nav("/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed");
    }
    setLoading(false);
  };

  if (cart.length === 0) { nav("/shop"); return null; }

  const businessName = content?.business_name || "Condor & Eagle Trading Post";
  const contactPhone = content?.contact_phone;
  const contactEmail = content?.contact_email;
  const contactAddress = content?.contact_address;

  const onlineMethodsAvailable = !!payCfg?.square_configured;
  const cashAppAvailable = onlineMethodsAvailable && !!payCfg?.cash_app_pay_enabled;
  const cashAppTagAvailable = !!payCfg?.cashapp_cashtag;

  const methods = [
    { v: "cash_pickup", label: "Cash at Pickup", sub: "Pay cash in-store" },
    { v: "card_pickup", label: "Card at Pickup", sub: "Swipe at the register" },
    onlineMethodsAvailable && { v: "square_card", label: "Pay Online — Card", sub: "Secure card payment via Square" },
    cashAppAvailable && { v: "cash_app_pay", label: "Pay with Cash App (Square)", sub: "Embedded Cash App Pay button — auto-verified" },
    cashAppTagAvailable && { v: "cashapp_tag", label: `Send to Cash App $${payCfg.cashapp_cashtag}`, sub: `Open Cash App and send the total to ${payCfg.cashapp_display_name || '$'+payCfg.cashapp_cashtag}` },
  ].filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="checkout-page">
      <h1 className="font-display text-5xl mb-8">CHECKOUT</h1>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs block mb-1">FULL NAME</label>
            <input required value={form.customer_name} onChange={e=>setForm({...form, customer_name:e.target.value})} className="input-field" data-testid="name-input"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-1">PHONE</label>
            <input required value={form.customer_phone} onChange={e=>setForm({...form, customer_phone:e.target.value})} className="input-field" data-testid="phone-input"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-2">PAYMENT METHOD</label>
            <div className="space-y-2" data-testid="payment-options">
              {methods.map(o => (
                <label key={o.v} className={`flex items-start gap-3 p-3 border-2 cursor-pointer ${form.payment_method===o.v?'border-[var(--accent)] bg-[var(--muted)]':'border-[var(--ink)]'}`} data-testid={`pm-${o.v}`}>
                  <input type="radio" name="pm" checked={form.payment_method===o.v} onChange={()=>setForm({...form, payment_method:o.v})} className="mt-1"/>
                  <div>
                    <div className="font-display">{o.label}</div>
                    <div className="font-mono text-xs text-[var(--ink-2)]">{o.sub}</div>
                  </div>
                </label>
              ))}
            </div>
            {!onlineMethodsAvailable && (
              <div className="font-mono text-[10px] text-[var(--ink-2)] mt-2">Online payments are not configured yet — contact the store owner.</div>
            )}
          </div>

          {form.payment_method === "square_card" && (
            <div className="card-industrial p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider mb-2">Card details</div>
              <div ref={cardRef} id="card-container" data-testid="square-card-container" className="min-h-[80px]"/>
            </div>
          )}
          {form.payment_method === "cash_app_pay" && (
            <div className="card-industrial p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider mb-2">Cash App Pay</div>
              <div ref={cashAppRef} id="cash-app-pay-container" data-testid="cash-app-pay-container" className="min-h-[60px]"/>
              <div className="font-mono text-[10px] text-[var(--ink-2)] mt-2">Tap the button, then approve in your Cash App.</div>
            </div>
          )}

          <div>
            <label className="font-mono text-xs block mb-1">NOTES (OPTIONAL)</label>
            <textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} rows="3" className="input-field" data-testid="notes-input"/>
          </div>
          <div className="bg-[var(--muted)] border-2 border-[var(--ink)] p-4 font-mono text-sm space-y-1">
            <strong>PICKUP:</strong> {contactAddress || "Address on file with owner"}<br/>
            {contactPhone && <><strong>CALL:</strong> {contactPhone} · </>}{contactEmail && <><strong>EMAIL:</strong> {contactEmail}</>}<br/>
            <span>We'll call when your order is ready.</span>
          </div>
        </div>
        <div className="card-industrial p-6 h-fit">
          <h2 className="font-display text-2xl mb-4 border-b-2 border-[var(--ink)] pb-2">ORDER</h2>
          {cart.map(i => <div key={i.product_id} className="flex justify-between font-mono text-sm mb-2"><span>{i.name} ×{i.qty}</span><span>${(i.price*i.qty).toFixed(2)}</span></div>)}
          <div className="flex justify-between font-display text-2xl border-t-2 border-[var(--ink)] pt-3 mt-4"><span>TOTAL</span><span>${total.toFixed(2)}</span></div>
          <button disabled={loading} className="btn-primary w-full mt-6" data-testid="place-order-btn">
            {(() => {
              if (loading) return "PROCESSING...";
              if (form.payment_method === "square_card") return `PAY $${total.toFixed(2)}`;
              if (form.payment_method === "cash_app_pay") return `PAY WITH CASH APP $${total.toFixed(2)}`;
              if (form.payment_method === "cashapp_tag") return `OPEN CASH APP · $${total.toFixed(2)}`;
              return "PLACE ORDER";
            })()}
          </button>
          <div className="font-mono text-[10px] text-[var(--ink-2)] mt-2 text-center">{businessName}</div>
        </div>
      </form>
    </div>
  );
}
